import { Integer } from "@type-r/ext-types";
import {
  attributes,
  AttributesMixin,
  define,
  Model,
  type,
  Events,
  Collection
} from "@type-r/models";
import * as moment from "moment";
import {
  matchString,
  UploadSupportModel,
  ChangeTransaction
} from "./common";
import { restfulIO, fetchJson, fetchResponse } from "./endpoint";
import { localUserInfo } from "./localUserInfo";
import { OptionalOrgInfo, OrgInfo } from "./orgInfo";
import {
  ParticipantRoles,
  InvoiceManagementCustomerApprovalRoles,
  InvoiceManagementCustomerRead,
  InvoiceManagementCustomerWrite,
  InvoiceManagementSupplierApprovalRoles,
  InvoiceManagementSupplierRead,
  InvoiceManagementSupplierWrite,
  InvoiceManagementPermission
} from "./permissions";
import { ShippingInfo, ShippingNotice } from "./shipping";
import { Document } from "./uploads";
import { PartModel } from "./commonModels";
import { getUsersByPermissions } from "./localUserInfo";
import { globalStore } from "./store";
import { InvoiceDispute } from "./dispute";
import { TextFilter, MultiSelectFilter, DateRangeFilter, BooleanFilter, useLinkedQueryParam } from 'lqx-react';
import { RelationshipModel, RelationshipUserInfo } from "./relationship";
import { Activity } from "./activity";

export type Address = OrgInfo["address"];
export type ContactInfo = OrgInfo["contact"];

export interface InvoiceModel extends AttributesMixin<typeof InvoiceModel> {
  invoiceStatus: InvoiceStatus;
  disputeStatus: CalculatedDisputeStatus;
}

const InvoiceStatusTextMapping = {
  ApprovalNeeded: "Approval Needed",
  AwaitingRemittance: "Awaiting Remittance",
  PaymentInTransit: "Payment in Transit",
  Reconciled: "Reconciled",
  PaymentComplete: "Payment Complete",

  // dispute
  DisputedWithCustomer: "Disputed w/ Customer",
  DisputedWithSupplier: "Disputed w/ Supplier"
};
export const GetInvoiceStatusAsText = (
  status: CalculatedDisputeStatus | InvoiceStatus
) => {
  return InvoiceStatusTextMapping[status] || `Unknown status(${status})`;
};

export type InvoiceStatus =
  | "ApprovalNeeded"
  | "AwaitingRemittance"
  | "PaymentInTransit"
  | "Reconciled"
  | "PaymentComplete";

export type CalculatedDisputeStatus =
  | "DisputedWithCustomer"
  | "DisputedWithSupplier";

const supprotedActivityTypes = ["Invoice_INSERTED", "Invoice_UPDATED", "Invoice_CommentAdded", "Invoice_PromiseToPay_UPDATED", "Invoice_SentDate_UPDATED","Invoice_SOC_Invoicing_SENT", "InvoiceInternalStatus", "InvoiceMarkAsSent", "InvoiceMarkAsConfirmed"];
@define
export class InvoiceEvent extends Model {
  static attributes = {
    firstName: "",
    lastName: "",
    email: "",
    event: "",
    timeStamp: Date,
    actorShortCompanyName: String,
    disputeHistoryId: Number,
    attachmentId: Number,
    comment: String,
    documentType: String,
    disputeTransaction: type(ChangeTransaction),
    updateTransaction: type(ChangeTransaction)
  };

  get author() {
    if (this.email && this.email.indexOf("AUTOMATED_ACTION") === 0) {
      return "Automated action";
    }

    const auth = [
      this.firstName,
      this.lastName,
      this.email ? "<" + this.email + ">" : ""
    ]
      .join(" ")
      .trim();
    return this.actorShortCompanyName
      ? `${this.actorShortCompanyName} - ${auth}`
      : auth;
  }
}
export interface InvoicesTotals
  extends AttributesMixin<typeof InvoicesTotals> {}

@define
export class InvoicesTotals extends Model {
  static endpoint = restfulIO("/api/v2/invoices/totals");

  static attributes = {
    pending: Number,
    disputed: Number,
    paymentinprogress: Number,
    complete: Number
  };

  get all(): Number {
    return (
      this.pending + this.disputed + this.paymentinprogress + this.complete
    );
  }
}

@define
export class InvoiceReconciliationInfo extends Model {
   static endpoint = restfulIO("/api/v2/recs/invoice/reconciliation-info");

   static attributes = {
       invoiceId : String,
       reconciliationId : String,
       paymentId : String,
       reconciledAmount : Number,
       paymentAmount : Number,
       discountAmount : Number,
       paymentValueDate : Date,
       reconciliationBookedDate : Date,
       reconciliationCreatedOn : Date,
   }
}

export interface InvoiceReconciliationInfo extends AttributesMixin<typeof InvoiceReconciliationInfo>{}

export interface InvoiceEvent extends AttributesMixin<typeof InvoiceEvent> {}

@define
export class InvoiceModel extends Model implements UploadSupportModel {
  static endpoint = restfulIO("/api/v2/invoices");

  static attributes = {
    //# list, get, and put
    id: String,
    invoiceNumber: String,
    poNumber: String,
    soNumber: String,
    packingSlipNumber: String,
    quoteId: "QuoteIdPlaceholder",
    salesOrderId: "SalesOrderIdPlaceholder",
    currency: String,
    invoicedAmount: Number,
    invoicedAmountFx: Number,
    outstandingInvoiceAmountFx: Number,
    invoiceSentDateUtc: type(Date).null,
    createdOnUtc: Date,
    invoiceDateUtc: Date,
    invoiceDueDateUtc: Date,
    lastUpdatedUtc: Date, // TODO: Include this in the DB Model and API
    paymentCompletionDateUtc: Date, // TODO: Include this in the DB Model and API
    paymentDateUtc: Date, // TODO: Include this in the DB Model and API
    projectedPaymentDate: Date,
    invoiceCreatedDateUtc: Date,
    invoiceStatus: type(String).dontSave,
    disputeStatus: type(String).dontSave,
    paymentPeriod: Integer,
    discountPeriod: Integer,
    paymentTerms: Integer,
    purchaseOrdersId: Integer,
    interchangeControlNumber: String,
    interchangeDate: String,
    isaTimestamp: String,
    // TODO: Dispute props - included in API
    activeDisputeId: Number,
    disputeAging: 13,
    conversationId: Number,
    promiseToPayDateUtc: type(Date).null.dontSave,
    outstandingInvoiceAmount: Number,
    externalStatus: String,
    externalStatusDescription: String,
    internalStatusId: Number,

    ship_to: type(OrgInfo).null,
    bill_to: type(OptionalOrgInfo).null,
    end_customer: type(OptionalOrgInfo).null,

    disputedAmount: Number,

    shippingAccount: String,
    shippingCarrier: String,
    shippingMethod: String,
    shippingInstructions: String,

    dispute: attributes({
      poNumber: String,
      invoiceDueDateUtc: type(Date).null.dontSave,
      outstandingAmount: type(Number).null
    }),
    isEscalatedInternally: type(Boolean).null.dontSave,
    disputeActiveSideRole: type(String).null.dontSave,
    initialActorUserType: type(String).null.dontSave,

    parts: type([PartModel]).dontSave.endpoint(restfulIO("./parts")),

    disputes: type([InvoiceDispute]).dontSave.endpoint(restfulIO("./dispute")),

    shipment: type([ShippingNotice])
      .dontSave.endpoint(restfulIO("./shipment"))
      .parse(json => {
        // TODO: move parsing here from ShippingNotice model
        return json;
      })
      ,

    uploads: type([Document]).dontSave.endpoint(restfulIO("./uploads")),

    logs: type([InvoiceEvent]).dontSave.endpoint(restfulIO("./logs")),
    activites: type([Activity]).dontSave.endpoint(restfulIO("./activity")),
    reconciliationInvoiceInfos: type([InvoiceReconciliationInfo]).dontSave,

    relationshipUsers: type([RelationshipUserInfo]).dontSave,
    remittanceAdviceIds : type(Array).dontSave,
    source: type(String).null.dontSave,
    hasSocComments: type(Boolean).dontSave,
    matchedPayments: type(Collection).dontSave,
    paymentInfoIds: type(Array).dontSave,
    createdBy: String,
    updatedBy: String,

  };

  get unreconciledAmount() {
    return this.outstandingInvoiceAmount;
  }

  get invoiceDueDate() {
    return this.invoiceDueDateUtc;
  }

  get age() {
    const date = this.invoiceDueDate;
    return Math.trunc((new Date().setHours(0, 0, 0, 0) - date.getTime()) / 86400000);
  }

  get promiseToPayDate() {
    return this.promiseToPayDateUtc;
  }

  static readPermissionFilter(x: InvoiceModel): boolean {
    return localUserInfo.isAdminRead || x.isSupplier || x.isCustomer;
  }

  get hasActiveDispute(): boolean {
    return this.disputes.some(x => x.isActiveDispute);
  }

  get getActiveDispute(): InvoiceDispute | null {
    return this.disputes.find(x => x.isActiveDispute);
  }

  static diffNames = {
    poNumber: "PO #",
    invoiceDueDateUtc: "Invoice Due Date",
    outstandingAmount: "Outstanding Amount",
    partNumber: "Part #",
    quantity: "Qty",
    price: "Unit Cost"
  };

  toJSON(options?) {
    const json = super.toJSON(options);
    if (options && options.includeAttr) {
      for (const prop of options.includeAttr) {
        json[prop] = this[prop].toJSON();
      }
    }

    return json;
  }

  get shipping(): ShippingInfo {
    return new ShippingInfo({
      account: this.shippingAccount,
      carrier: this.shippingCarrier,
      method: this.shippingMethod,
      instructions: this.shippingInstructions
    });
  }

  get category(): InvoiceCategory {
    switch (this.calculatedStatus) {
      case "DisputedWithCustomer":
      case "DisputedWithSupplier":
        return "disputed";

      case "AwaitingRemittance":
      case "ApprovalNeeded":
        return "pending";

      case "PaymentInTransit":
      case "Reconciled":
        return "paymentinprogress";

      case "PaymentComplete":
        return "complete";

      default:
        return "unknown";
    }
  }

  get calculatedStatus(): CalculatedDisputeStatus | InvoiceStatus {
    return this.disputeStatus || this.invoiceStatus;
  }

  async fetch(options?: {}) {
    await Promise.all([
      super.fetch(options),
      this.shipment.fetch().catch(e => {}), //no PO then no shipment information
      this.parts.fetch().catch(e => {}), //no PO then no parts
      this.uploads.fetch().catch(e => {}), //no PO then no uploads
      this.logs.fetch(),
      this.activites.fetch({params:{resolveUsers:true}}),
      this.reconciliationInvoiceInfos.fetch({params:{id:this.id}}).catch(e => {})
    ]).catch(e => {
      Events.trigger("navigate", "/invoices");
    });

    try {
      await this.disputes.fetch({
        params: {
          invoiceStatus: this.invoiceStatus
        }
      });
    } catch (e) {
      // there are no disputes
      // this.disputes = [];
    }

    if (this.id && this.canAccessMailList) {
      this.relationshipUsers = await getUsersByPermissions(this.conversationId, {
        InvoiceManagement: allDialogsSendMailListRoles
      }) as any;
    }

    if (this.activites && this.activites.length > 0) {
        const activityTypes = await Activity.getActivityTypes();

        const supportedActivities: InvoiceEvent[]  = this.activites.reduce((acc, activity) => {
            const commonElement = activityTypes.find(e=> e.name.toLowerCase() === activity.type.toLowerCase()
            && supprotedActivityTypes
                .map(item=>item.toLowerCase())
                .includes(e.name.toLowerCase()));

            if (commonElement ){
                acc.push(new InvoiceEvent({email: (activity.userInfo && activity.userInfo.email) || activity.createdBy,
                                        comment: activity.comment,
                                        timeStamp: activity.createdOn,
                                        event: commonElement.description || activity.comment}));
            }
        return acc
        }, []);

        this.logs.add(supportedActivities);
    }
    return this;
  }

  async doUpload(file, comment, uploadType, documentType) {
    let fileId = 0;
     let documentTypeValue = ' ';
     if(documentType){
       documentTypeValue = documentType.value;
     }
    try {
      const [upload] = this.uploads.add({
        file_name: file.name,
        file_comments: comment,
        purchase_orders_id: this.purchaseOrdersId.toString(),
        uploadInvoiceStatus: this.invoiceStatus,
        user_id: localUserInfo.id,
        relationshipParticipantUserId: this.relationshipParticipantUserId.toString(),
        document_type: documentTypeValue
      });

      await upload.save({
        params: { file }
      });

      fileId = upload.id;
    } finally {
      await Promise.all([this.logs.fetch(), this.uploads.fetch()]);
    }

    return fileId;
  }

  get relationship(){
    return globalStore.getRelationship(this.conversationId);
  }

  get customer(){
    const { relationship } = this;
    return relationship && relationship.customer;
  }

  get supplier(){
    const { relationship } = this;
    return relationship && relationship.supplier;
  }

  get supplierName(): string {
    const { relationship } = this;
    const supplier = relationship && relationship.Supplier;
    return !!supplier && (supplier.companyShortname || supplier.companyName);
  }

  get customerName(): string {
    const { relationship } = this;
    const customer = relationship && relationship.Customer;
    return !!customer && (customer.companyShortname || customer.companyName);
  }

  get title(): string {
    const titleComponents = [this.customerName, this.supplierName].filter(
      x => x
    );
    const conversation = globalStore.conversations.get(
      this.conversationId as any
    );

    return titleComponents.join(" - ");
  }

  static categories: InvoiceCategory[] = [
    "disputed",
    "pending",
    "paymentinprogress",
    "complete",
    "unknown"
  ];

  match(text: string) {
    return (
      !text ||
      text.length < 2 ||
      matchString(this.poNumber, text) ||
      matchString(this.salesOrderId, text) ||
      matchString(this.invoiceNumber, text) ||
      matchString(this.quoteId, text) ||
      matchString(this.packingSlipNumber, text) ||
      matchString(this.invoiceStatus, text) ||
      matchString(this.supplierName, text) ||
      matchString(this.customerName, text)
    );
  }

  static sortBy = {
    invoiceStatus: "Invoice Status",
    invoiceNumber: "INV #",
    poNumber: "PO #",
    invoicedAmount: "INV AMT",
    invoiceDueDateUtc: "Invoice Due Date",
    anticipatedPaymentDateUtc: "Promise To Pay",
    activeDisputeId: "DISP #",
    disputeAging: "DISP AGING",
    paymentDateUtc: "Payment Date",
    lastUpdatedUtc: "Last Updated",
    paymentCompletionDateUtc: "Payment Completion Date",
    createdOn: "Created On"
  };

  get isApprovalNeededStatus(): boolean {
    return this.invoiceStatus === "ApprovalNeeded";
  }

  get isAwaitingRemittanceStatus(): boolean {
    return this.invoiceStatus === "AwaitingRemittance";
  }

  get isPaymentInTransitStatus(): boolean {
    return this.invoiceStatus === "PaymentInTransit";
  }

  get isReconciledStatus(): boolean {
    return this.invoiceStatus === "Reconciled";
  }

  get isDisputedWithCustomerStatus(): boolean {
    return this.disputeStatus === "DisputedWithCustomer";
  }

  get isDisputedWithSupplierStatus(): boolean {
    return this.disputeStatus === "DisputedWithSupplier";
  }

  get isDisputedStatus (): boolean{
    return this.isDisputedWithCustomerStatus || this.isDisputedWithSupplierStatus;
  }

  get relationshipParticipantUserId(): Number {
    return localUserInfo.getRelationshipParticipantUserId(this.conversationId);
  }

  get isSupplier(): boolean {
    return localUserInfo.hasSomePermissions(
      "InvoiceManagement",
      ParticipantRoles.Supplier,
      InvoiceManagementSupplierRead,
      this.conversationId
    );
  }
  get isCustomer(): boolean {
    return localUserInfo.hasSomePermissions(
      "InvoiceManagement",
      ParticipantRoles.Customer,
      InvoiceManagementCustomerRead,
      this.conversationId
    );
  }

  get isSupplierWrite(): boolean {
    return localUserInfo.hasSomePermissions(
      "InvoiceManagement",
      ParticipantRoles.Supplier,
      [InvoiceManagementPermission.SupplierWrite],
      this.conversationId
    );
  }
  get isCustomerWrite(): boolean {
    return localUserInfo.hasSomePermissions(
      "InvoiceManagement",
      ParticipantRoles.Customer,
      [InvoiceManagementPermission.CustomerWrite],
      this.conversationId
    );
  }
  get isSuperSupplier(): boolean {
    return localUserInfo.hasSomePermissions(
      "InvoiceManagement",
      ParticipantRoles.Supplier,
      [InvoiceManagementPermission.SupplierSuperSupplier],
      this.conversationId
    );
  }
  get isCustomerApprover(): boolean {
    return localUserInfo.hasSomePermissions(
      "InvoiceManagement",
      ParticipantRoles.Customer,
      [InvoiceManagementPermission.CustomerApprover],
      this.conversationId
    );
  }
  get isSupplierApprover(): boolean {
    return localUserInfo.hasSomePermissions(
      "InvoiceManagement",
      ParticipantRoles.Supplier,
      [InvoiceManagementPermission.SupplierApprover],
      this.conversationId
    );
  }
  get isParticipantApprover(): boolean {
    return (
      localUserInfo.hasSomePermissions(
        "InvoiceManagement",
        ParticipantRoles.Customer,
        InvoiceManagementCustomerApprovalRoles,
        this.conversationId
      ) ||
      localUserInfo.hasSomePermissions(
        "InvoiceManagement",
        ParticipantRoles.Supplier,
        InvoiceManagementSupplierApprovalRoles,
        this.conversationId
      )
    );
  }

  get hasWritePermissions(): boolean {
    return (
      localUserInfo.isAdminWrite ||
      localUserInfo.hasSomePermissions(
        "InvoiceManagement",
        ParticipantRoles.Supplier,
        InvoiceManagementSupplierWrite,
        this.conversationId
      ) ||
      localUserInfo.hasSomePermissions(
        "InvoiceManagement",
        ParticipantRoles.Customer,
        InvoiceManagementCustomerWrite,
        this.conversationId
      )
    );
  }

  get canAccessMailList(): boolean {
    return (
      localUserInfo.isAdminWrite ||
      localUserInfo.hasSomePermissions(
        "InvoiceManagement",
        ParticipantRoles.Supplier,
        canAccessMailSupplierPermissions,
        this.conversationId
      ) ||
      localUserInfo.hasSomePermissions(
        "InvoiceManagement",
        ParticipantRoles.Customer,
        canAccessMailCustomerPermissions,
        this.conversationId
      )
    );
  }

  mailList(mailList: InvoiceActionType): string[] {
    const isSupplier =
      this.isSupplier &&
      // for approve and dispute mail lists Super Supplier should see the mail list as a customer when
      // invoice is in DisputeWithCustomer status or when we don't have active dispute
      !(
        (mailList === "approve" || mailList === "dispute") &&
        this.isSuperSupplier &&
        (this.disputeStatus === "DisputedWithCustomer" ||
          !this.hasActiveDispute)
      );
    const mailFilter = ru =>
      ru.permissions["InvoiceManagement"] &&
      ru.permissions["InvoiceManagement"].some(x =>
        isSupplier
          ? mailListMap[mailList]["supplier"].includes(x)
          : mailListMap[mailList]["customer"].includes(x)
      );
    return this.relationshipUsers.filter(mailFilter).map(ru => ru.userName);
  }

  // tslint:disable-next-line:member-ordering
  static createFilter(
    myActionItems: boolean,
    advancedSearchFilter: string,
    showAdvancedSearch: boolean
  ) {
    const myActionItemsFilter = invoice => {
      if (myActionItems) {
        const role = localUserInfo.user_type;
        const statuses = myActionItemsRoleMapping[role];

        if (statuses) {
          return statuses.includes(invoice.invoiceStatus);
        }
      }
      return true;
    };

    const advancedSearch = invoice => {
      if (showAdvancedSearch && advancedSearchFilter) {
        const asf = JSON.parse(advancedSearchFilter);
        const result =
          (asf.status
            ? asf.status.map(x => x.value).includes(invoice.invoiceStatus)
            : true) &&
          (asf.customer
            ? asf.customer.map(x => x.value).includes(invoice.customerLegalName)
            : true) &&
          (asf.supplier
            ? asf.supplier.map(x => x.value).includes(invoice.supplierLegalName)
            : true) &&
          (asf.date
            ? dateInRange(invoice[asf.date.value], asf.startDate, asf.endDate)
            : true) &&
          (asf.daysDue
            ? inRange(invoice.attributes.invoiceDueDateUtc, asf.daysDue)
            : true);
        return result;
      }
      return true;
    };

    return x => myActionItemsFilter(x) && advancedSearch(x);
  }

  get canBeAccepted(): boolean {
    return this.isApprovalNeededStatus && !this.activeDisputeId;
  }

  get canEditPromiseToPay(): boolean {
    return this.isAwaitingRemittanceStatus && this.isParticipantApprover && !this.hasActiveDispute;
  }

  get displayStatus() :string {
    return GetInvoiceStatusAsText(this.disputeStatus || this.invoiceStatus)
  }

  async accept(doPatch: boolean = false) {
    if (!this.promiseToPayDateUtc) {
      return;
    }

    this.promiseToPayDateUtc = this.refreshTime(this.promiseToPayDateUtc);

    const data: AcceptInvoicesData = {
      acceptedInvoiceIds: [this.id],
      promiseToPayDate: this.promiseToPayDateUtc
    };

    const response =  await acceptInvoices(data, doPatch);
    this.updateInvoiceFromAcceptData(response[0]);
  }

  refreshTime = (input:Date) : Date =>{
    var result= new Date();
    result.setFullYear(input.getFullYear());
    result.setMonth(input.getMonth());
    result.setDate(input.getDate());
    return result;
  }

  updateInvoiceFromAcceptData({
    invoiceStatus,
    lastUpdatedUtc,
    promiseToPayDateUtc
  }: AttributesMixin<typeof InvoiceModel>) {
    this.invoiceStatus = invoiceStatus as InvoiceStatus;
    this.lastUpdatedUtc = lastUpdatedUtc;
    this.promiseToPayDateUtc = promiseToPayDateUtc;
  }

  applyDisputeChanges(disputeEntry: InvoiceDispute, maxHistoryVersion: number) {
    const history = disputeEntry.history.filter(
      h => h.versionNumber <= maxHistoryVersion
    );
    if (!disputeEntry.isActiveDispute || disputeEntry.isUserOnTheActiveSide) {
      history.forEach(h => {
        this.set(h.change, { parse: true, remove: false, merge: true });
      });
      return;
    }

    let possibleChanges = [];
    history.forEach(historyEntry => {
      possibleChanges.push(historyEntry.change);
      if (historyEntry.status === "Disputed") {
        possibleChanges.forEach(h => {
          this.set(h, { parse: true, remove: false, merge: true });
        });
        possibleChanges = [];
      }
    });

    if (
      this.isSuperSupplier &&
      (history.pop().activeSideRole === "Customer") === this.isCustomer
    ) {
      possibleChanges.forEach(h => {
        this.set(h, { parse: true, remove: false, merge: true });
      });
    }
  }

  static async search(data) {
    const url = `/api/v2/invoices/search`;
    return await fetchJson('POST', url, data);
  }

  static async close(invoiceIds){
    const url = `/api/v2/invoices/close`;
    return await fetchJson('PUT', url, invoiceIds);
  }

  get showPromiseToPayDate() : boolean {
    let showPromiseToPayDateStatuses = [
      'AwaitingRemittance',
      'PaymentInTransit',
      'Reconciled',
      'PaymentComplete'];

      return this.promiseToPayDateUtc && showPromiseToPayDateStatuses.includes(this.invoiceStatus);
  }

  async sendInvoiceEmail(uploads){
    const url = `/api/v2/invoices/${this.id}/send-invoicing-email`;
    await fetchResponse('POST', url, uploads);
  }

    static async getInternalStatusesByCompany(relationshipId) {
        //1393 for flex as supper_supplier
        const url = `/api/v2/invoice-management/internal-invoice-status/relationship/${relationshipId}`;
        return await fetchJson('GET', url);
    }

    async saveInternalInvoiceStatuses(statusInformation) {
        const url = `/api/v2/invoice-management/internal-invoice-status/save-status/invoice/${statusInformation.invoiceId}`;;
        return await fetchResponse('PUT', url, statusInformation);
    }

    static async saveInternalInvoicesStatus(invoicesStatusInformation) {
        const url = `/api/v2/invoice-management/internal-invoice-status/save-statuses/`;
        return await fetchResponse('PUT', url, invoicesStatusInformation);
    }
}

export interface AcceptInvoicesData {
  promiseToPayDate: Date;
  acceptedInvoiceIds: string[];
}

export function acceptInvoices(
  data: AcceptInvoicesData, doPatch:boolean = false
): Promise<AttributesMixin<typeof InvoiceModel>[]> {
  return doPatch ? fetchJson("POST", "/api/v2/invoices/promisetopay", data) : fetchJson("POST", "/api/v2/invoices/accept", data);
}

const myActionItemsRoleMapping = {
  distributor: ["AwaitingRemittance", "ApprovalNeeded", "DisputedWithCustomer"],
  supplier: ["DisputedWithSupplier"],
  supersupplier: [
    "AwaitingRemittance",
    "ApprovalNeeded",
    "DisputedWithCustomer",
    "DisputedWithSupplier"
  ]
};

const allCategories = {
  disputed: true,
  pending: true,
  paymentinprogress: true,
  complete: true,
  unknown: true
};

const inRange = (date, rangeStr) => {
  if (!valid(date)) return false;

  const range = parseInt(rangeStr);
  const invDate = moment(date);
  const today = moment.now();

  const result = Math.abs(invDate.diff(today, "days"));
  return result <= range;
};

const dateInRange = (date, start, end) => {
  if (!valid(date)) return false;

  date = date.setHours(0, 0, 0, 0);
  let startDate = new Date(start).setHours(0, 0, 0, 0);
  let endDate = valid(end)
    ? new Date(end).setHours(0, 0, 0, 0)
    : new Date("1/1/2099").setHours(0, 0, 0, 0);
  let swap = endDate;
  if (endDate < startDate) {
    endDate = startDate;
    startDate = swap;
  }

  if (startDate <= date && date <= endDate) return true;
  else return false;
};

const valid = x => {
  return x && x != null;
};

export type InvoiceColor = "blue" | "green" | "grey" | "yellow" | "red";
export type InvoiceCategory =
  | "disputed"
  | "pending"
  | "paymentinprogress"
  | "complete"
  | "unknown";

export type InvoiceActionType =
  | "dispute"
  | "approve"
  | "escalateInternally"
  | "update"
  | "reject"
  | "sendBack"
  | "close"
  | "internalStatus";

const canAccessMailCustomerPermissions = [
  InvoiceManagementPermission.CustomerWrite,
  InvoiceManagementPermission.CustomerApprover
];

const canAccessMailSupplierPermissions = [
  InvoiceManagementPermission.SupplierWrite,
  InvoiceManagementPermission.SupplierSuperSupplier,
  InvoiceManagementPermission.SupplierApprover
];

const allDialogsSendMailListRoles = [
  InvoiceManagementPermission.SupplierWrite,
  InvoiceManagementPermission.SupplierApprover,
  InvoiceManagementPermission.CustomerWrite,
  InvoiceManagementPermission.CustomerApprover
];

// CUSTOMER
const disputeCustomerSendMailListRoles = [
  InvoiceManagementPermission.SupplierWrite,
  InvoiceManagementPermission.SupplierApprover
];
const approveCustomerSendMailListRoles = disputeCustomerSendMailListRoles;
const updateCustomerSendMailListRoles = disputeCustomerSendMailListRoles;
const submitEscalationCustomerSendMailListRoles = disputeCustomerSendMailListRoles;
const escalateInternallyCustomerSendMailListRoles = [
  InvoiceManagementPermission.CustomerApprover
];
const rejectCustomerSendMailListRoles = [
  InvoiceManagementPermission.CustomerWrite
];
const sendBackCustomerSendMailListRoles = rejectCustomerSendMailListRoles;

// SUPPLIER
const disputeSupplierSendMailListRoles = [
  InvoiceManagementPermission.CustomerWrite,
  InvoiceManagementPermission.CustomerApprover
];
const approveSupplierSendMailListRoles = disputeSupplierSendMailListRoles;
const updateSupplierSendMailListRoles = disputeSupplierSendMailListRoles;
const submitEscalationSupplierSendMailListRoles = disputeSupplierSendMailListRoles;
const escalateInternallySupplierSendMailListRoles = [
  InvoiceManagementPermission.SupplierApprover
];
const rejectSupplierSendMailListRoles = [
  InvoiceManagementPermission.SupplierWrite
];
const sendBackSupplierSendMailListRoles = rejectSupplierSendMailListRoles;

const mailListMap = {
  dispute: {
    supplier: disputeSupplierSendMailListRoles,
    customer: disputeCustomerSendMailListRoles
  },
  approve: {
    supplier: approveSupplierSendMailListRoles,
    customer: approveCustomerSendMailListRoles
  },
  escalateInternally: {
    supplier: escalateInternallySupplierSendMailListRoles,
    customer: escalateInternallyCustomerSendMailListRoles
  },
  update: {
    supplier: updateSupplierSendMailListRoles,
    customer: updateCustomerSendMailListRoles
  },
  reject: {
    supplier: rejectSupplierSendMailListRoles,
    customer: rejectCustomerSendMailListRoles
  },
  sendBack: {
    supplier: sendBackSupplierSendMailListRoles,
    customer: sendBackCustomerSendMailListRoles
  }
};

export interface InvoiceFilters extends AttributesMixin<typeof InvoiceFilters> {}
@define
export class InvoiceFilters extends Model {
    static attributes = {
        filterAll : '',
        customerName: MultiSelectFilter,
        supplierName: MultiSelectFilter,
        invoiceAmount: TextFilter,
        invoiceStatus: MultiSelectFilter,
        invoiceDueDate: DateRangeFilter,
        promiseToPayDate: DateRangeFilter,
        invoiceSentDate: DateRangeFilter,
        invoiceNotSent:BooleanFilter
    }

    invoiceStatuses = ["ApprovalNeeded", "AwaitingRemittance", "PaymentInTransit", "Reconciled", "PaymentComplete", "DisputedWithCustomer", "DisputedWithSupplier"];

    initializeOptions(relationships: Collection<RelationshipModel>) {
        this.filterAll = '';
        const customers = relationships.map(r => r.Customer && r.Customer.companyName);
        this.customerName.set({selected: [], options: Array.from(new Set(customers.filter(x => x)))});
        const supplierNames = relationships.map(r => r.Supplier && r.Supplier.companyName);
        this.supplierName.set({selected: [], options: Array.from(new Set(supplierNames.filter(x => x)))});
        this.invoiceAmount.set({value: undefined});

        const statuses = this.invoiceStatuses.map(s => GetInvoiceStatusAsText(s as InvoiceStatus));
        this.invoiceStatus.set({selected: [], options: Array.from(new Set(statuses.filter(x => x)))});
    }

    mapToInvoiceQuery(sort, category, relationships:Collection<RelationshipModel>) {
      var filteredConvIds = globalStore.getFilteredConversationIdsByCompanyName(this.filterAll);
      var customerNames = this.customerName && this.customerName.selected;
      var supplierNames = this.supplierName && this.supplierName.selected;
      var selectedStatusesAsText = this.invoiceStatus && this.invoiceStatus.selected;

      var customerIds = customerNames && relationships
        .filter(r => r.Customer && customerNames.includes(r.Customer.companyName))
        .map(r => r.Customer.companyId);

      var supplierIds = supplierNames && relationships.models
        .filter(r => r.Supplier && supplierNames.includes(r.Supplier.companyName))
        .map(r => r.Supplier.companyId);
      var selectedStatuses = selectedStatusesAsText &&
        Object.keys(InvoiceStatusTextMapping).filter(key => selectedStatusesAsText.includes(InvoiceStatusTextMapping[key]));

     return {
        searchString: this.filterAll && this.filterAll.length > 2 ? this.filterAll : null,
        invoiceAmount: this.invoiceAmount && this.invoiceAmount.value ? this.invoiceAmount.value : null,
        customerIds : Array.from(new Set(customerIds)),
        supplierIds : Array.from(new Set(supplierIds)),

        invoiceStatuses:Array.from(new Set(selectedStatuses)),
        invoiceDueDateRange: this.toRange(this.invoiceDueDate),
        promiseToPayDateRange:this.toRange(this.promiseToPayDate),
        invoiceSentDateRange:this.toRange(this.invoiceSentDate),
        invoiceNotSent:this.invoiceNotSent && this.invoiceNotSent.value ? this.invoiceNotSent.value : null,

        bucketFilter: this.toBucketFilter(),

        partial: true,
        sortBy: sort.attr,
        asc: sort.asc,
        filteredConvIds: filteredConvIds,
        conversationIds: [],
        dateField: null,
        startDate:null,
        endDate: null,
        skip: 0,
        take: 1000,
        ...(category !== "all" && { category: category, searchString: this.filterAll })
      };
    }

    toRange(dateRangeFilter: DateRangeFilter) {
      return dateRangeFilter && (dateRangeFilter.from && dateRangeFilter.to) ?
        { from : dateRangeFilter.from, to : dateRangeFilter.to}
        : null
    }

    toBucketFilter() {
      const $relationshipId = useLinkedQueryParam("relationshipId");
      const $bucket = useLinkedQueryParam("bucket");
      const $failedReason = useLinkedQueryParam("failedReason");

      return $relationshipId.value && $bucket.value
        ? {
          relationshipId: $relationshipId.value,
          bucket: $bucket.value,
          failedReason: $failedReason.value
        }
        : null;
    }
}
